import { Link } from '@tanstack/react-router';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { TOOLS } from '../../data/toolCatalog';
import { TOOL_SEO } from '../../data/toolSeo';
import { Band } from '../../components/editorial/Band';
import { Eyebrow } from '../../components/Eyebrow';
import { m } from '../../paraglide/messages';

/**
 * The free tools that run on this platform, listed on the platform's own page.
 *
 * Every tool page links out to `/api/<platform>` through `ToolSeoFooter`, and until
 * now nothing linked back: none of the platform pages named a single tool. That left
 * most tools reachable from exactly two places on the site, and Google had never
 * crawled five of them at all. This is the return leg.
 *
 * `TOOL_SEO[slug].apiId` is already the platform route id, so the pairing has one
 * owner and cannot drift from the link the tool page publishes.
 */
const toolsForPlatform = (apiId: string) =>
  TOOLS.filter((tool) => TOOL_SEO[tool.to.replace('/tools/', '')]?.apiId === apiId);

export const PlatformTools = ({ apiId, platform }: { apiId: string; platform: string }) => {
  const tools = toolsForPlatform(apiId);
  if (!tools.length) return null;

  return (
    <Band>
      <div className="band" aria-label={m.platform_tools_aria({ platform })}>
        <Eyebrow to="/tools">{m.platform_tools_eyebrow()}</Eyebrow>
        <h2 className="display-3 mt-4 text-text">{m.platform_tools_heading({ platform })}</h2>
        <p className="lede mt-5 max-w-[62ch]">
          {m.platform_tools_lede()}
        </p>
        {/* No outer box: the cells carry the rules, so a row that does not fill
            ends where its last tool does instead of framing an empty cell. */}
        <div className="mt-8 overflow-hidden border-t border-rule">
          <div className="-mr-px grid grid-cols-1 s480:grid-cols-2">
            {tools.map((tool) => (
              <Link
                key={tool.to}
                to={tool.to}
                className="group flex min-w-0 flex-col gap-1 border-b border-r border-rule px-4 py-3.5 transition-colors hover:bg-wash"
              >
                <span className="text-14 font-medium tracking-[-0.01em] group-hover:text-accent">
                  {tool.title}
                </span>
                <span className="truncate text-13 text-text-3">{tool.description}</span>
              </Link>
            ))}
          </div>
        </div>
        <p className="mt-6 text-15 text-text-2">
          <ParaglideMessage
            message={m.platform_tools_browse_all}
            markup={{
              link: ({ children }) => (
                <Link to="/tools" className="link-dotted">
                  {children}
                </Link>
              ),
            }}
          />
        </p>
      </div>
    </Band>
  );
};
